// /src/components/imovel/CardDestaquePreview.jsx
// VERSÃO 11.0 SUPREMA - TIM UNITY DOSSIER (RESOLVEDOR DE MÍDIA DINÂMICO)

import React, { useState, useMemo, useEffect } from 'react';
import { 
    FiGlobe, FiSmartphone, FiPhone, FiArrowRight, FiMonitor, 
    FiShield, FiDollarSign, FiChevronLeft, FiChevronRight, 
    FiGrid, FiStar, FiZap, FiLayout, FiFileText, FiClock, FiX
} from 'react-icons/fi'; 
import { motion, AnimatePresence } from 'framer-motion';

// 📡 RESOLVEDOR DE IMAGENS TÁTICO
const resolveImagePath = (path) => {
    if (!path) return 'https://via.placeholder.com/1200x800?text=AGUARDANDO+MÍDIA+TIM';
    if (path.startsWith('http')) return path;
    
    const formattedPath = path.startsWith('/') ? path : `/${path}`;
    
    // Sincronização com o Backend via Env ou Fallback
    const baseUrl = import.meta.env.VITE_API_BASE_URL 
        ? import.meta.env.VITE_API_BASE_URL.replace('/api', '') 
        : ''; 
        
    return `${baseUrl}${formattedPath}`;
};

const fmt = (val) => new Intl.NumberFormat('pt-BR', { 
    style: 'currency', currency: 'BRL', maximumFractionDigits: 2 
}).format(val || 0);

export default function CardDestaquePreview({ imovel, onClose }) {
    const [activeCategory, setActiveCategory] = useState('todos');
    const [activeIdx, setActiveIdx] = useState(0);

    // 🎯 LÓGICA DE NEGÓCIO ADAPTATIVA (Telecom)
    const tipo = imovel?.tipoNegocio || 'Linhas novas';
    const valorPlano = imovel?.valores?.venda || imovel?.valorVenda || 0;
    
    // Extração do Arsenal de Benefícios
    const ligacoes = imovel?.caracteristicas?.quartos || imovel?.quartos || 'Não';
    const roaming = imovel?.caracteristicas?.areaTotal || imovel?.areaTotal || 'Não';
    const fidelidade = imovel?.valores?.iptu || imovel?.valorIptu || '24 Meses';
    const appsInclusos = imovel?.caracteristicas?.vagasGaragem || imovel?.vagasGaragem || '--';
    const cobertura = imovel?.nivelCobertura || 'Brasil';

    const categorias = useMemo(() => ({
        todos: [...(imovel?.imagensDestaque || []), ...(imovel?.imagensQuartos || []), ...(imovel?.imagensBanheiros || []), ...(imovel?.imagensOutros || [])],
        fachada: imovel?.imagensDestaque || [],   // Imagem principal
        quartos: imovel?.imagensQuartos || [],    // Benefícios
        banheiros: imovel?.imagensBanheiros || [],// Como funciona
        social: imovel?.imagensOutros || []       // Status
    }), [imovel]);

    const currentGallery = useMemo(() => {
        const gal = categorias[activeCategory] || [];
        return gal.length > 0 ? gal : categorias.todos;
    }, [categorias, activeCategory]);

    useEffect(() => { setActiveIdx(0); }, [activeCategory]);

    const nextImg = () => setActiveIdx((prev) => (prev + 1) % currentGallery.length);
    const prevImg = () => setActiveIdx((prev) => (prev === 0 ? currentGallery.length - 1 : prev - 1));

    if (!imovel) return null;

    return (
        <div className="relative w-full max-w-[1600px] mx-auto rounded-[3.5rem] overflow-hidden border shadow-2xl transition-all duration-700 border-[var(--border-color)] bg-[var(--bg-card)]">
            
            {/* 🚪 BOTÃO SAIR */}
            {onClose && (
                <button 
                    onClick={onClose}
                    className="absolute top-8 right-8 z-50 p-4 rounded-full bg-black/10 backdrop-blur-xl border border-white/10 text-[var(--text-secondary)] hover:text-[var(--accent-color)] hover:border-[var(--accent-color)] hover:scale-110 active:scale-95 transition-all duration-300 group"
                >
                    <FiX size={24} className="group-hover:rotate-90 transition-transform duration-500" />
                </button>
            )}

            {/* 🛰️ HEADER: TERMINAL DE INSPEÇÃO */}
            <header className="flex flex-col md:flex-row items-center justify-between p-10 gap-6 border-b border-[var(--border-color)] bg-[var(--bg-card)]">
                <div className="flex items-center gap-6">
                    <div className="w-14 h-14 rounded-2xl border flex items-center justify-center shadow-inner"
                         style={{ backgroundColor: 'var(--bg-main)', borderColor: 'var(--border-color)', color: 'var(--accent-color)' }}>
                        <FiMonitor size={24} />
                    </div>
                    <div>
                        <h3 className="text-auto font-black uppercase tracking-tighter leading-none mb-1 text-2xl">Dossiê do Plano TIM</h3>
                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-accent opacity-80">Análise de Oferta Corporativa</p>
                    </div>
                </div>

                {/* FILTRO POR ZONAS (GLASS DESIGN) */}
                <div className="flex bg-black/10 backdrop-blur-xl border border-[var(--border-color)] p-1.5 rounded-2xl overflow-x-auto no-scrollbar mr-12">
                    {[
                        { id: 'todos', label: 'Tudo', icon: FiGrid },
                        { id: 'fachada', label: 'Plano', icon: FiStar },
                        { id: 'quartos', label: 'Benefícios', icon: FiZap },
                        { id: 'banheiros', label: 'Operação', icon: FiLayout },
                        { id: 'social', label: 'Status', icon: FiShield }
                    ].map(cat => (categorias[cat.id]?.length > 0 || cat.id === 'todos') && (
                        <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase transition-all whitespace-nowrap
                                ${activeCategory === cat.id ? 'text-black shadow-xl' : 'text-[var(--text-secondary)] opacity-40 hover:opacity-100'}`}
                            style={activeCategory === cat.id ? { backgroundColor: 'var(--accent-color)' } : {}}
                        >
                            <cat.icon size={12} /> {cat.label}
                        </button>
                    ))}
                </div>
            </header>

            <div className="p-10 md:p-16">
                {/* 📽️ FRAME CINEMATOGRÁFICO */}
                <div className="relative h-[500px] md:h-[700px] mb-16 rounded-[4rem] overflow-hidden border border-[var(--border-color)] shadow-2xl bg-black">
                    <AnimatePresence mode="wait">
                        <motion.img 
                            key={activeIdx + activeCategory}
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            transition={{ duration: 0.8 }}
                            // USANDO O RESOLVEDOR AQUI
                            src={resolveImagePath(currentGallery[activeIdx])} 
                            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                        />
                    </AnimatePresence>

                    <div className="absolute inset-0 flex justify-between items-center px-10 pointer-events-none text-white">
                        <NavBtn icon={FiChevronLeft} onClick={prevImg} />
                        <NavBtn icon={FiChevronRight} onClick={nextImg} />
                    </div>

                    <div className="absolute bottom-10 right-10 text-right bg-black/60 backdrop-blur-3xl border border-white/10 p-10 rounded-[3rem] shadow-2xl">
                        <span className="text-white/30 text-[10px] font-black uppercase tracking-[0.5em] block mb-3">Investimento Mensal</span>
                        <div className="space-y-2 text-white">
                            <div className="flex flex-col">
                                <span className="text-[8px] font-black text-accent uppercase" style={{ color: 'var(--accent-color)' }}>Assinatura</span>
                                <div className="flex items-baseline gap-2 justify-end">
                                    <h2 className="text-5xl font-black tracking-tighter leading-none">{fmt(valorPlano)}</h2>
                                    <span className="text-sm opacity-40 font-bold">/MÊS</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-8 space-y-12">
                        <div className="space-y-4">
                            <h1 className="text-auto leading-[0.85] text-5xl md:text-6xl font-black uppercase tracking-tighter">{imovel.titulo}</h1>
                            <div className="flex items-center gap-4 text-[var(--text-secondary)] opacity-60 font-bold uppercase tracking-[0.3em] text-sm">
                                <FiGlobe className="text-accent" style={{ color: 'var(--accent-color)' }} size={18} /> 
                                COBERTURA: {cobertura} {cobertura === 'Cidades' ? `(${imovel.localizacao?.cidade || imovel.cidade})` : ''}
                            </div>
                        </div>

                        <div className="space-y-6">
                            <h5 className="text-[11px] font-black uppercase tracking-[0.6em] text-accent flex items-center gap-3" style={{ color: 'var(--accent-color)' }}>
                                <FiFileText size={16} /> Relatório Descritivo
                            </h5>
                            <div className="border-l-2 border-[var(--border-color)] pl-10">
                                <p className="text-lg leading-relaxed text-auto opacity-70 font-medium whitespace-pre-wrap italic">
                                    "{imovel.descricao || 'Este plano aguarda a homologação da narrativa comercial.'}"
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-4 bg-[var(--bg-main)] border border-[var(--border-color)] rounded-[3.5rem] p-12 space-y-10 h-fit shadow-inner">
                        <h5 className="text-[10px] font-black uppercase tracking-[0.4em] text-[var(--text-secondary)] opacity-30 border-b border-[var(--border-color)] pb-6 text-center">
                            Detalhes da Oferta
                        </h5>
                        <div className="space-y-8">
                            <SpecRow icon={FiPhone} label="Ligações" value={ligacoes === 'Sim' ? 'Ilimitadas' : 'Limitadas'} />
                            <SpecRow icon={FiGlobe} label="Roaming" value={roaming === 'Sim' ? 'Nacional' : 'Local'} />
                            <SpecRow icon={FiSmartphone} label="Apps Inclusos" value={appsInclusos} />
                            <SpecRow icon={FiClock} label="Fidelidade" value={fidelidade} />
                            <SpecRow icon={FiZap} label="Tipo de Oferta" value={tipo} />
                        </div>
                        <div className="pt-6">
                            <div className="w-full py-5 rounded-2xl font-black uppercase text-[11px] tracking-[0.3em] shadow-xl text-center flex items-center justify-center gap-3 transition-transform hover:scale-[1.02]"
                                 style={{ backgroundColor: 'var(--accent-color)', color: '#000' }}>
                                <FiShield /> Oferta Homologada TIM
                            </div>
                        </div>
                    </div>
                </div>

                <footer className="mt-24 pt-12 border-t border-[var(--border-color)] flex justify-between items-center opacity-20 text-auto">
                    <span className="text-[10px] font-black uppercase tracking-[1em]">TIM UNITY Engine v11.0</span>
                    <FiShield size={24} />
                </footer>
            </div>
        </div>
    );
}

const NavBtn = ({ icon: Icon, onClick }) => (
    <button onClick={onClick} className="p-6 rounded-full backdrop-blur-xl bg-white/10 text-white/40 pointer-events-auto hover:bg-[var(--accent-color)] hover:text-black hover:scale-110 active:scale-90 transition-all shadow-2xl border border-white/20">
        <Icon size={32} />
    </button>
);

const SpecRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-center justify-between group transition-all">
        <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-[var(--bg-card)] border border-[var(--border-color)] flex items-center justify-center text-accent shadow-sm group-hover:scale-110 transition-transform">
                <Icon size={18} style={{ color: 'var(--accent-color)' }} />
            </div>
            <span className="text-[11px] font-bold uppercase text-[var(--text-secondary)] opacity-40 tracking-widest">{label}</span>
        </div>
        <span className="text-base font-black text-auto uppercase tracking-tighter truncate max-w-[150px] text-right" title={value}>{value || '--'}</span>
    </div>
);